import { useQuery, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { ChannelGroupResource, EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

import ChannelsList from './ChannelsList';
import CsvImportErrors from './CsvImportErrors';
import FormPageGroupSwitch from './FormPageGroupSwitch';
import ImportOptionsField from './ImportOptionsField';
import ShouldShuffleField from './ShouldShuffleField';

const Dialog = styled(StyledDialog)`
  width: 632px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const ContentHolder = styled.div`
  max-height: calc(90vh - 220px);
  overflow-y: auto;
  ${scrollBarStyles};
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

const isValidLink = (url?: string, article = false) => {
  if (!url) {
    return false;
  }

  if (!url.includes('https')) {
    return false;
  }

  if (article) {
    return url.includes('boredpanda.com');
  }

  return true;
};

export type FormDataT = {
  file: File | null;
  page_id: number;
  option: string;
  channel_group_id: number | null;
  should_shuffle: boolean;
};

interface CsvRow {
  article_link?: string;
  comment?: string;
  image?: string;
  images?: string;
  message?: string;
  site_visit_benchmark?: string;
  type?: string;
  video?: string;
  page_id?: string;
  group_id?: string;
  pinterest_board_id?: string;
}

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const ImportModal: FC<Props> = ({ activePage, onClose }) => {
  const [isChannelGroups, setIsChannelGroups] = useState(false);

  const alerts = useAlerts();
  const toasts = useToasts();
  const [csvErrors, setCsvErrors] = useState<{ [rowNumber: number]: string }>(
    {},
  );

  const queryClient = useQueryClient();

  const [network, networkLabel] = useNetwork();

  const {
    control,
    formState: { isSubmitting },
    setError,
    clearErrors,
    watch,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      file: null,
      page_id: activePage.id,
      option: 'append',
      should_shuffle: false,
    },
  });

  const { data } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
    },
  );

  const pages = useMemo(() => {
    switch (network) {
      case EditorialFacebookPostNetwork.INSTAGRAM:
        return data?.filter((p) => !!p.instagramAccountId);
      case EditorialFacebookPostNetwork.THREADS:
        return data?.filter((p) => !!p.threadsAccountId);
      case EditorialFacebookPostNetwork.PINTEREST:
        return data?.filter((p) => !!p.pinterestAccountId);
      case EditorialFacebookPostNetwork.TWITTER:
        return data?.filter((p) => !!p.twitterAccountId);
      case EditorialFacebookPostNetwork.WHATSAPP:
        return data?.filter((p) => !!p.whatsAppNewsletterId);
      default:
        return data;
    }
  }, [data, network]);

  const ensureCsvIsValid = (file: File | null) => {
    return new Promise<boolean>((res, rej) => {
      if (!file) {
        alerts.error('CSV file is not uploaded!');
        return res(false);
      }

      Papa.parse(file, {
        delimiter: ',',
        header: true,
        complete: (results) => {
          const errors: { [row: number]: string } = {};
          const rows = results.data as CsvRow[];

          rows.forEach((row, index) => {
            const rowNumber = index + 1;
            switch (row.type) {
              case 'link':
                if (!row.article_link) {
                  errors[rowNumber] = 'article_link column is empty';
                  break;
                }
                if (!isValidLink(row.article_link, true)) {
                  errors[rowNumber] = 'article_link is invalid';
                  break;
                }
                if (!row.message) {
                  errors[rowNumber] = 'message column is empty';
                  break;
                }
                break;
              case 'image':
                if (!row.image) {
                  errors[rowNumber] = 'image column is empty';
                  break;
                }
                if (!isValidLink(row.image)) {
                  errors[rowNumber] = 'image column is invalid';
                  break;
                }
                if (
                  network === EditorialFacebookPostNetwork.PINTEREST &&
                  !row.pinterest_board_id
                ) {
                  errors[rowNumber] = 'pinterest_board_id is required!';
                  break;
                }
                if (
                  network === EditorialFacebookPostNetwork.TWITTER &&
                  !row.article_link
                ) {
                  errors[rowNumber] = 'link is required!';
                  break;
                }
                break;
              case 'video':
              case 'reel':
                if (network === EditorialFacebookPostNetwork.TWITTER) {
                  errors[rowNumber] =
                    'type reel or video is not supported for Twitter';
                  break;
                }
                if (!row.video) {
                  errors[rowNumber] = 'video column is empty';
                  break;
                }
                if (!isValidLink(row.video)) {
                  errors[rowNumber] = 'video column is invalid';
                  break;
                }
                if (!row.message) {
                  errors[rowNumber] = 'message column is empty';
                  break;
                }
                break;
              case 'text':
                if (
                  ![
                    EditorialFacebookPostNetwork.FACEBOOK,
                    EditorialFacebookPostNetwork.TEXT_STATUS,
                    EditorialFacebookPostNetwork.REDDIT,
                  ].includes(network)
                ) {
                  errors[rowNumber] =
                    'type text is not supported for this social network';
                  break;
                }
                if (!row.message) {
                  errors[rowNumber] = 'message column is empty';
                  break;
                }
                break;
              case 'story':
                if (network !== EditorialFacebookPostNetwork.FACEBOOK) {
                  errors[rowNumber] =
                    'type story is not supported for this social network';
                  break;
                }
                if (!row.image) {
                  errors[rowNumber] = 'image column is empty';
                  break;
                }
                if (!row.article_link) {
                  errors[rowNumber] = 'article_link column is empty';
                  break;
                }
                break;
              case 'album':
                if (
                  [EditorialFacebookPostNetwork.FACEBOOK].includes(network)
                ) {
                  errors[rowNumber] =
                    'type album is not supported for this social network';
                  break;
                }
                if (!row.images) {
                  errors[rowNumber] = 'images column is empty';
                  break;
                }
                break;
              case 'placeholder':
                break;
              default:
                errors[rowNumber] = 'Invalid type column';
                break;
            }
          });

          setCsvErrors(errors);

          res(!Object.keys(errors).length);
        },
      });
    });
  };

  const { data: channelGroups } = useQuery(
    ['channel_groups'],
    () =>
      api.get<never, ChannelGroupResource[]>(`/v1/distribution/channel-groups`),
    {
      staleTime: 1000 * 60,
    },
  );

  const channelGroup = watch('channel_group_id');

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const isValid = await ensureCsvIsValid(data.file);

        if (!isValid) {
          return;
        }

        const formData = new FormData();

        if (isChannelGroups) {
          if (!!data.channel_group_id) {
            formData.append(
              'channel_group_id',
              data.channel_group_id.toString(),
            );
          }
        } else {
          formData.append('page_id', data.page_id.toString());
        }
        formData.append('option', data.option);
        formData.append('network', network.toString());
        formData.append('should_shuffle', data.should_shuffle ? '1' : '0');

        if (data.file) {
          formData.append('file', data.file);
        }

        await api.post('/v1/editorial/queue/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
        ]);

        onClose();

        toasts.create('success', 'Posts imported');
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Import file</Heading>
        <ContentHolder>
          <FormLayout>
            <FormField<FormDataT>
              id="csv-file-input"
              type="file"
              accept="text/csv"
              control={control}
              notes="Format: CSV"
              name="file"
            />
            <CsvImportErrors errors={csvErrors} />
            <Larger>
              <Label>Network</Label>
              <NetworkInput readOnly value={networkLabel} />
            </Larger>
            {network === EditorialFacebookPostNetwork.FACEBOOK && (
              <Larger>
                <FormPageGroupSwitch
                  isChannelGroups={isChannelGroups}
                  setIsChannelGroups={setIsChannelGroups}
                />
              </Larger>
            )}
            {!!isChannelGroups &&
            network === EditorialFacebookPostNetwork.FACEBOOK ? (
              <>
                <FormField<FormDataT, number>
                  control={control}
                  type="select"
                  name="channel_group_id"
                  label="Page Group"
                  options={
                    channelGroups?.map((group) => {
                      return {
                        label: group.name,
                        value: group.id,
                      };
                    }) ?? []
                  }
                />
                {!!channelGroup && !!channelGroups?.length && (
                  <Larger>
                    <ChannelsList
                      currentGroup={channelGroups.find(
                        (group) => group.id === channelGroup,
                      )}
                    />
                  </Larger>
                )}
              </>
            ) : (
              <>
                <FormField<FormDataT, number>
                  type="select"
                  key="page_name"
                  control={control}
                  name="page_id"
                  label="Page Name"
                  placeholder="Select"
                  options={
                    pages?.map((p) => ({ value: p.id, label: p.name })) ?? []
                  }
                />
              </>
            )}
            <ImportOptionsField control={control} />
            <Larger>
              <Label>Additional features</Label>
              <ShouldShuffleField control={control} />
            </Larger>
          </FormLayout>
        </ContentHolder>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Upload
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ImportModal;
